import { useCallback, useMemo } from "react";

import {
    AppBar,
    Authenticator,
    CircularProgressCenter,
    Drawer,
    FireCMS,
    FireCMSi18nProvider,
    type ListenCollectionDelegateProps,
    ModeControllerProvider,
    NavigationRoutes,
    Scaffold,
    SideDialogs,
    SnackbarProvider,
    useBuildLocalConfigurationPersistence,
    useBuildModeController,
    useBuildNavigationController,
    useTranslation,
    useValidateAuthenticator,
} from "@firecms/core";
import {
    FirebaseAuthController,
    FirebaseLoginView,
    FirebaseSignInProvider,
    FirebaseUserWrapper,
    useFirebaseAuthController,
    useFirebaseStorageSource,
    useFirestoreDelegate,
    useInitialiseFirebase,
} from "@firecms/firebase";
import { CenteredView } from "@firecms/ui";
import { streams } from "@one-ai/shared/cms/streams";

import { announcementsCollection } from "./collections/announcements";
import { casesCollection } from "./collections/cases";
import { articleCollection } from "./collections/article";
import { tagCollection } from "./collections/tag";
import { LocaleSwitcher } from "./components/LocaleSwitcher";
import { collectionStringsFor } from "./i18n/collection-strings";
import { cmsTranslations, defaultCmsLocale } from "./i18n/locales";
// Side-effect import: registers every collection-string bundle before any
// collection is built.
import "./i18n/collections";
import { firebaseConfig } from "./firebaseConfig";
import { matchesFilter } from "./lib/searchFilter";
import { publishedSideSwitchPlugin } from "./plugins/publishedSideSwitch";
import { tagSearchControllerBuilder } from "./lib/tagSearch";

/** Kept in sync by hand with `isAdmin()` in /firestore.rules. */
const ALLOWED_EMAIL_DOMAIN = "@one-ai.inc";

/**
 * Google only. The rules require `email_verified`, and password sign-in would
 * let someone reach a signed-in UI whose every write then fails — a confusing
 * failure to debug. Google always supplies a verified address.
 */
const signInOptions: FirebaseSignInProvider[] = ["google.com"];

function CmsApp() {
    /**
     * A usability measure, not the security boundary: this runs in the browser
     * and is bypassable. `isAdmin()` in /firestore.rules is what actually
     * protects the data. Its job is to fail the login cleanly instead of
     * letting someone in to a UI where every save is rejected.
     */
    const companyAuthenticator: Authenticator<FirebaseUserWrapper> =
        useCallback(async ({ user }) => {
            if (!user?.emailVerified) return false;
            return Boolean(user.email?.endsWith(ALLOWED_EMAIL_DOMAIN));
        }, []);

    /**
     * Rebuilt whenever the editor switches language.
     *
     * `useTranslation` subscribes this component to i18next's
     * `languageChanged`, so the re-render is what re-runs the factories; the
     * memo key is the language rather than `[]`. This is the reason `CmsApp`
     * exists as a component *inside* `FireCMSi18nProvider` — the provider
     * renders an `I18nextProvider`, so a hook above it would see no locale at
     * all.
     *
     * One AI's four are the only ones an editor sees — an article collection
     * and its own tag collection per stream, built from `streams` rather than
     * listed, so a new stream appears in the drawer with no edit here. Each
     * stream's tags sit directly below its articles, because a taxonomy is
     * something an editor visits occasionally rather than the thing they came
     * for.
     *
     * OneLive's `announcements` and `cases` are **deprecated as of 2026-09-03**
     * and stay registered only so their data remains reachable — each sets
     * `hideFromNavigation` and refuses writes, and each carries the full
     * reasoning at the top of its own file.
     */
    const { i18n } = useTranslation();
    const collections = useMemo(() => {
        const strings = collectionStringsFor(i18n.language);
        return [
            announcementsCollection(strings),
            casesCollection(strings),
            ...streams.flatMap((stream) => [
                articleCollection(stream, strings),
                tagCollection(stream, strings),
            ]),
        ];
    }, [i18n.language]);

    const { firebaseApp, firebaseConfigLoading, configError } =
        useInitialiseFirebase({ firebaseConfig });

    const modeController = useBuildModeController();

    const authController: FirebaseAuthController = useFirebaseAuthController({
        firebaseApp,
        signInOptions,
    });

    const userConfigPersistence = useBuildLocalConfigurationPersistence();

    /**
     * Firestore has no text search, so the bar `tags` turns on with
     * `textSearchEnabled` needs a controller to answer it. Ours indexes the two
     * `label` locales and the slug; FireCMS's own `localTextSearchEnabled`
     * would index neither of the first two. It answers for the `tags` path
     * only, and reports every other path unsupported — see `lib/tagSearch.ts`.
     */
    const baseDelegate = useFirestoreDelegate({
        firebaseApp,
        textSearchControllerBuilder: tagSearchControllerBuilder,
    });

    /**
     * The delegate, with the filters its text search drops put back.
     *
     * `listenCollection` receives a `filter` whether or not a search string is
     * set — `useDataSourceTableController` seeds it from `forceFilter` — but
     * the Firestore delegate branches into `performTextSearch` before it builds
     * a query, and that branch takes only a path and a search string. So every
     * filter stops applying the moment an editor types, and the tag picker's
     * `forceFilter: { retired: ["==", false] }` stops holding with it.
     *
     * Filtering the results as they come back is the only place to fix that:
     * the search branch returns ids from an index, not a query anything can add
     * a `where` to. It is deliberately generic rather than a retired-tag
     * special case — a column filter combined with a search string in any
     * listing view has the same hole.
     *
     * **This overrides one method of a FireCMS-internal contract**, so a
     * `@firecms/firebase` bump is a place to re-read `listenCollection`. The
     * failure mode if its shape changes is a type error here, not a silent one.
     */
    const firestoreDelegate = useMemo(() => {
        const listenCollection = baseDelegate.listenCollection;
        if (!listenCollection) return baseDelegate;
        return {
            ...baseDelegate,
            listenCollection: <
                M extends Record<string, unknown> = Record<string, unknown>,
            >(
                props: ListenCollectionDelegateProps<M>,
            ) => {
                const { searchString, filter, onUpdate } = props;
                if (!searchString || !filter) {
                    return listenCollection(props);
                }
                const keep = matchesFilter<M>(filter);
                return listenCollection<M>({
                    ...props,
                    onUpdate: (entities) => onUpdate(entities.filter(keep)),
                });
            },
        };
    }, [baseDelegate]);

    // Required by FireCMS even though nothing uses it: no property in either
    // collection has a `storage` config, because image fields are plain URLs
    // (see "Deliberately out of scope" in the design doc). Adding an upload
    // field later needs only a `storage` block on that property.
    const storageSource = useFirebaseStorageSource({ firebaseApp });

    const { authLoading, canAccessMainView, notAllowedError } =
        useValidateAuthenticator({
            authController,
            authenticator: companyAuthenticator,
            dataSourceDelegate: firestoreDelegate,
            storageSource,
        });

    const navigationController = useBuildNavigationController({
        disabled: authLoading,
        collections,
        authController,
        dataSourceDelegate: firestoreDelegate,
        /**
         * Plugins are passed here rather than to `FireCMS`, whose `plugins`
         * prop is deprecated and logs a warning.
         *
         * `publishedSideSwitchPlugin` exists only to move one field of one
         * collection into the entity form's side column — see its own file for
         * why a collection definition cannot do that.
         */
        plugins: [publishedSideSwitchPlugin],
    });

    if (firebaseConfigLoading || !firebaseApp) {
        return <CircularProgressCenter />;
    }

    if (configError) {
        return <CenteredView>{configError}</CenteredView>;
    }

    return (
        <SnackbarProvider>
                <ModeControllerProvider value={modeController}>
                    <FireCMS
                        navigationController={navigationController}
                        authController={authController}
                        userConfigPersistence={userConfigPersistence}
                        dataSourceDelegate={firestoreDelegate}
                        storageSource={storageSource}
                    >
                        {({ loading }) => {
                            if (loading || authLoading) {
                                return (
                                    <CircularProgressCenter size={"large"} />
                                );
                            }

                            if (!canAccessMainView) {
                                return (
                                    <FirebaseLoginView
                                        authController={authController}
                                        firebaseApp={firebaseApp}
                                        signInOptions={signInOptions}
                                        notAllowedError={notAllowedError}
                                    />
                                );
                            }

                            return (
                                <Scaffold autoOpenDrawer={false}>
                                    <AppBar title={"One AI Content"}>
                                        <LocaleSwitcher />
                                    </AppBar>
                                    <Drawer />
                                    <NavigationRoutes />
                                    <SideDialogs />
                                </Scaffold>
                            );
                        }}
                    </FireCMS>
            </ModeControllerProvider>
        </SnackbarProvider>
    );
}

/**
 * The i18n boundary.
 *
 * `FireCMSi18nProvider` has to sit *above* everything that reads a locale, and
 * it returns `null` until i18next has initialised — so `CmsApp` and its hooks
 * only ever run with a resolved locale, and nothing has to handle a
 * "translations not ready" state.
 *
 * `locale` here is only the default for an editor who has never chosen: the
 * provider prefers a stored `firecms_locale` whenever one exists, and
 * `LocaleSwitcher` is what writes it. `translations` is re-applied when it
 * changes, so adding a locale needs no remount.
 */
function App() {
    return (
        <FireCMSi18nProvider
            locale={defaultCmsLocale}
            translations={cmsTranslations}
        >
            <CmsApp />
        </FireCMSi18nProvider>
    );
}

export default App;
